import React, { useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ChevronRight, Check, X, Loader, Globe, Search, Wrench } from "lucide-react";
import { tokens } from "@/theme/tokens";
import { useEmbedUi } from "@/embed/EmbedUiContext";
import { headlineForMood, moodFromWorkEntries } from "./ThoughtBubble";
import type { Citation, WorkEntry } from "../../chat/types";

export interface WorkTimelineProps {
  /** The turn's ordered work timeline. When absent/empty, degraded entries are
   *  derived from thinking/toolCalls/workingNotes (old messages). */
  entries?: WorkEntry[];
  /** True while the turn is still running (expanded + "Working…" header). */
  streaming?: boolean;
  /** Reasoning duration (ms) → "Thought for Xs" when collapsed. */
  durationMs?: number;
  // Degraded-mode fallbacks (pre-workLog history messages):
  thinking?: string;
  toolCalls?: { name: string; label?: string; isError?: boolean }[];
  workingNotes?: string[];
  citations?: Citation[];
  /** Start collapsed even while streaming. Users can still expand manually.
   *  Default false = open while streaming, auto-collapse when answer starts. */
  defaultCollapsed?: boolean;
}

/** Best-effort timeline for old messages persisted before workLog existed:
 *  one reasoning block (thinking), the narration notes, then a tool row per
 *  toolCall (no per-step results; citations, if any, attach to the last tool). */
export function deriveEntries(p: WorkTimelineProps): WorkEntry[] {
  if (p.entries && p.entries.length) return p.entries;
  const out: WorkEntry[] = [];
  if (p.thinking) out.push({ kind: "reasoning", text: p.thinking });
  for (const n of p.workingNotes ?? []) out.push({ kind: "narration", text: n });
  for (const t of p.toolCalls ?? []) {
    out.push({
      kind: "tool",
      name: t.name,
      label: t.label ?? t.name,
      status: t.isError ? "error" : "done",
    });
  }
  // Attach any loose citations to the last tool entry so old web turns still
  // show their sources somewhere in the timeline.
  if (p.citations && p.citations.length) {
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i].kind === "tool") {
        out[i] = { ...(out[i] as Extract<WorkEntry, { kind: "tool" }>), results: p.citations };
        break;
      }
    }
  }
  return out;
}

const ToolStatusIcon = ({ status }: { status: "running" | "done" | "error" }) =>
  status === "running" ? <Loader size={13} className="wp-spin" /> :
  status === "error" ? <X size={13} /> : <Check size={13} />;

const ToolKindIcon = ({ name }: { name: string }) =>
  name === "web_search" ? <Globe size={13} /> :
  name === "search_knowledge" ? <Search size={13} /> : <Wrench size={13} />;

/**
 * Displays an expandable timeline of reasoning, narration, and tool activity.
 *
 * @param props - Timeline entries and display state, including streaming status, duration, citations, and initial collapsed state.
 */
export function WorkTimeline(props: WorkTimelineProps) {
  const { compactDesign } = useEmbedUi();
  const reduce = useReducedMotion() ?? false;
  const streaming = !!props.streaming;
  const [open, setOpen] = useState(props.defaultCollapsed ? false : streaming);
  const userToggled = useRef(false);
  const prevStreaming = useRef(streaming);

  useEffect(() => {
    if (prevStreaming.current && !streaming && !userToggled.current) setOpen(false);
    if (!prevStreaming.current && streaming && !userToggled.current && !props.defaultCollapsed) setOpen(true);
    prevStreaming.current = streaming;
  }, [streaming, props.defaultCollapsed]);

  const entries = deriveEntries(props);
  // Compact embed: show friendly status copy while waiting for the first work-log
  // event. Early return must stay after all hooks (see hooks fix in history).
  const showEmptyCompact = compactDesign && streaming && entries.length === 0;
  if (entries.length === 0 && !showEmptyCompact) return null;

  const nSteps = entries.filter((e) => e.kind === "tool").length;
  const dur = typeof props.durationMs === "number" ? `${(props.durationMs / 1000).toFixed(1)}s` : null;
  const stepsSuffix = nSteps > 0 ? ` · ${nSteps} step${nSteps === 1 ? "" : "s"}` : "";
  const bubbleMood = moodFromWorkEntries(entries, streaming);
  const headline = compactDesign
    ? headlineForMood(bubbleMood, streaming, dur, stepsSuffix)
    : streaming
      ? "Working…"
      : (dur ? `Thought for ${dur}` : "Worked") + stepsSuffix;

  const toggle = () => { userToggled.current = true; setOpen((o) => !o); };

  return (
    <Box
      data-ph-scope="work_timeline"
      sx={(theme) => ({
        mt: 0, mb: 1, borderRadius: 1.75, border: 1,
        borderColor: streaming ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider,
        bgcolor: streaming ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.text.primary, 0.02),
        overflow: "hidden",
        transition: "border-color 200ms ease, background-color 200ms ease",
      })}
    >
      <Box
        component="button" type="button" onClick={toggle}
        data-ph={open ? "collapse" : "expand"}
        aria-label="Toggle work timeline" aria-expanded={open}
        sx={{
          display: "flex", alignItems: "center", gap: 0.75, width: "100%",
          border: 0, background: "transparent", cursor: "pointer", px: 1.5, py: 1,
          color: streaming ? "primary.main" : "text.secondary", font: "inherit", textAlign: "left",
          "&:focus-visible": (theme) => ({
            outline: "none",
            boxShadow: theme.palette.mode === "light" ? tokens.focusRing.light : tokens.focusRing.dark,
          }),
        }}
      >
        <motion.span style={{ display: "inline-flex" }} animate={{ rotate: open ? 90 : 0 }} transition={{ duration: reduce ? 0 : 0.2 }}>
          <ChevronRight size={15} />
        </motion.span>
        <Typography component="span" variant="caption"
          className={streaming && !reduce ? "artie-think-shimmer" : undefined}
          sx={{ fontWeight: 600, fontSize: 13 }}>
          {headline}
        </Typography>
      </Box>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.28, ease: [0.4, 0, 0.2, 1] }} style={{ overflow: "hidden" }}>
            <Box sx={{ px: 1.5, pb: 1.25, pl: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              {entries.map((e, i) => {
                if (e.kind === "reasoning" || e.kind === "narration") {
                  return (
                    <Typography key={i} variant="body2"
                      sx={{ color: "text.secondary", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.6 }}>
                      {e.text}
                    </Typography>
                  );
                }
                // tool entry
                return (
                  <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: e.status === "error" ? "error.main" : "text.secondary" }}>
                      <Box component="span" sx={{ display: "inline-flex" }}><ToolKindIcon name={e.name} /></Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{e.label}</Typography>
                      <Box component="span" sx={{ display: "inline-flex", ml: 0.25 }}><ToolStatusIcon status={e.status} /></Box>
                      {!!e.results?.length && (
                        <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
                          {e.results.length} result{e.results.length === 1 ? "" : "s"}
                        </Typography>
                      )}
                    </Box>
                    {!!e.results?.length && (
                      <Box component="ol" sx={{ pl: 3, my: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.25 }}>
                        {e.results.map((c, j) => {
                          const label = c.title ?? c.url ?? c.ref ?? `Source ${j + 1}`;
                          return (
                            <Box key={j} component="li" sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                              <Typography variant="caption" sx={{ color: "text.disabled", minWidth: 14 }}>{j + 1}.</Typography>
                              {c.url ? (
                                <Link data-ph="open_source" href={c.url} target="_blank" rel="noreferrer" variant="caption" sx={{ overflowWrap: "anywhere" }}>{label}</Link>
                              ) : (
                                <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>{label}</Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
